import type { ShopApiResponse } from '~/types/common/shop-api-response'
import type { GoodsDetailResponse } from '~/types/goods/goods-detail'
import { PR014 } from '~/enums/shop-goods-enums'
import type { BasketGoodsInfo, ShippingSavingInfo } from '~/types/goods/goods-item'
import {
  GoodsBasketApiError,
  UserAlertError,
  UserDialogError,
  UserToastError,
  UserValidationError,
} from '~/types/goods/errors'
import headerMenu from '~/composables/display/use-header-menu'

export const getGoodsDetail = async (query: object) => {
  const router = useRouter()
  const { $apiShop, $toast } = useNuxtApp()
  const { isLogin } = useAuthHandler()

  const getGoodsDetailRequest = $apiShop<GoodsDetailResponse>('/goods/v1/detail/goodsDetail', {
    method: 'GET',
    query,
  })
  const getDealGoodsInfo = $apiShop<ShopApiResponse<any>>('/display/v1/goods/getDealGoodsInfo', {
    method: 'GET',
    query,
  })
  const getPlanBannerList = $apiShop<ShopApiResponse<any>>('/display/v1/plan/planBannerList', {
    method: 'GET',
    query,
  })

  const requests = [getGoodsDetailRequest, getDealGoodsInfo, getPlanBannerList]

  if (isLogin.value) {
    const getMemberWishlist = $apiShop<ShopApiResponse<any>>('/member/v1/front/wishlistProducts', {
      method: 'POST',
      body: {
        goodsNo: [query.goodsNo],
      },
    })
    const getMemberCards = $apiShop<ShopApiResponse<any>>('/common/v1/eai/customer/custCardList', {
      method: 'GET',
    })
    requests.push(getMemberWishlist, getMemberCards)
  }

  const responses = await Promise.allSettled(requests) as ShopApiResponse<any>[]
  const [{
    value: { payload: goodsDetail } = {},
    status,
    reason,
  }, { value: { payload: dealGoodsInfo } = {} }, { value: { payload: planBannerList } = {} }] = responses
  if (status === 'rejected') {
    const { payload } = reason
    if (payload) {
      const { code } = payload
      if (code === '5003') {
        await router.replace('/goods/error/goods-not-for-sale')
        return
      }
      if (code === '7060' || code === '7010') {
        $toast.add({ text: '19세 이상만 구매 가능한 상품이에요', notification: { bottom: 104 } })
        router.back()
        return
      }
    }
    await router.replace('/common/system-error')
    return
  }

  if (dealGoodsInfo?.hotDealTimeDealYn === 'Y' && goodsDetail.prGoodsBase.goodsPrceTypCd === PR014.EVENT_PRICE) {
    goodsDetail.prGoodsBase.hotDealTimeDealYn = 'Y'
  }
  goodsDetail.goodsContentsDto.goodsPlanBannerList = planBannerList?.planInfoList || []

  if (isLogin.value) {
    const [{ value: { payload: wishlistInfo } = [], status1 }, {
      value: { payload: memberCardsStr } = '',
      status2,
    }] = responses.slice(3)
    if (status1 === 'rejected' || status2 === 'rejected') {
      await router.replace('/common/system-error')
      return
    }
    goodsDetail.likeYn = wishlistInfo[0].likeYn
    goodsDetail.prGoodsBase.hasScndCardYn = memberCardsStr.includes(goodsDetail.prGoodsBase.scndCardCd) ? 'Y' : 'N'
  }
  return goodsDetail
}
export const getGoodsOptions = (goodsNo: string) => {
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<any>>(`/goods/v2/detail/goods-option-info/${goodsNo}`, {
    method: 'GET',
  })
}
export const getGoodsItems = (goodsNo: string, query: object) => {
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<any>>(`/goods/v2/detail/goods-item-bases/${goodsNo}`, {
    method: 'GET',
    query,
  })
}
export const registerBasket = async (basketGoodsInfo: BasketGoodsInfo) => {
  const { $apiShop } = useNuxtApp()
  if (!basketGoodsInfo.ordGbCd || !basketGoodsInfo.prcTypCd || !basketGoodsInfo.infDispCtgNoGbCd) throw new UserToastError('필수값 없음')
  let response: ShopApiResponse<string[]> = {}
  try {
    response = await $apiShop<ShopApiResponse<string[]>>('/order/v1/basket/registerBasket', {
      method: 'POST',
      body: basketGoodsInfo,
    })
  }
  catch (e) {
    const { payload } = e
    if (payload) {
      const { code, message } = payload
      // 유효성 검증 실패
      const reloadCodes = [GoodsBasketApiError.NOT_ON_SALE, GoodsBasketApiError.BASKET_GOODS_SOLDOUT]
      if (reloadCodes.includes(code)) throw new UserAlertError(message, code, () => window.location.reload())
      if (code === GoodsBasketApiError.NOT_ORDER_CS_CENTER) throw new UserDialogError(message, code)
      if (code === GoodsBasketApiError.BASKET_GOODS_LIMIT_MULTI) throw new UserValidationError(message, code)
      if ([GoodsBasketApiError.BASKET_GOODS_LIMIT_MIN, GoodsBasketApiError.BASKET_GOODS_LIMIT_MAX, GoodsBasketApiError.BASKET_GOODS_LIMIT_ID, GoodsBasketApiError.BASKET_GOODS_LIMIT_ZERO].includes(code)) throw new UserToastError(message, code)
      if (code !== '0000') throw new UserAlertError(message, code)
    }
    throw e
  }

  // Header 장바구니 담기수 update
  await headerMenu().setCartValue()

  return response.payload
}
export const getShippingSavingGoodsList = (shippingSavingInfo: ShippingSavingInfo, paging: object) => {
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<GoodsDetailResponse>>(`/goods/v1/delivery/goods-shipping-saving`, {
    method: 'POST',
    body: {
      deliPolcNo: shippingSavingInfo.deliPolcNo,
      goodsNoList: shippingSavingInfo.goodsNoList,
      myScndCardList: shippingSavingInfo.myScndCardList,
      isScndCard: shippingSavingInfo.isScndCard,
      pageNo: paging.pageNo,
      pageSize: paging.pageSize,
    },
  })
}
export const getShippingSavingGoodsListCnt = (shippingSavingInfo: ShippingSavingInfo) => {
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<number>>(`/goods/v1/delivery/goods-shipping-saving-cnt`, {
    method: 'POST',
    body: {
      deliPolcNo: shippingSavingInfo.deliPolcNo,
      goodsNoList: shippingSavingInfo.goodsNoList,
      myScndCardList: shippingSavingInfo.myScndCardList,
      isScndCard: shippingSavingInfo.isScndCard,
    },
  })
}
export const getGoodsSummaryInfo = (goodsList: any[], query?: any) => {
  // paging 단위 변경
  const pageNo = 1
  const pageSize = 100
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<any>>(`/goods/v1/detail/summary`, {
    method: 'POST',
    body: {
      goodsNo: goodsList,
      pageNo,
      pageSize,
      ...query,
    },
  })
}
export const getBundledGoodsInfo = (goodsList: any[], query?: any) => {
  // paging 단위 변경
  const pageNo = 1
  const pageSize = 100
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<any>>(`/goods/v1/detail/packageSummary`, {
    method: 'POST',
    body: {
      goodsNo: goodsList,
      pageNo,
      pageSize,
      ...query,
    },
  })
}
export const getGoodsPriceCalcInfo = (goodsNo: string, query: object) => {
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<any>>(`/goods/v2/detail/goods-price-calc-info/${goodsNo}`, {
    method: 'GET',
    query,
  })
}
export const getRelationGoods = (goodsNo: string) => {
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<any>>(`/display/v1/goods/rel-goods-list`, {
    method: 'GET',
    query: { goodsNo },
  })
}
export const getSimilarGoods = (goodsNo: string) => {
  const { $apiShop } = useNuxtApp()
  return $apiShop<ShopApiResponse<any>>(`/display/v1/dummy/recommend/recom-goods-list`, {
    method: 'GET',
    query: { goodsNo },
  })
}
export const getSecondMaxBenefitInfo = async (goodsNo: string, itmNo: string, cnt?: number) => {
  const { $apiShop } = useNuxtApp()
  try {
    return await $apiShop<ShopApiResponse<any>>(`/goods/v1/detail/secondMaxDc/${goodsNo}`, {
      method: 'GET',
      query: { itmNo, cnt },
    })
  }
  catch (e) {
    console.error(e)
    const router = useRouter()
    await router.push('/common/system-error')
  }
}
